import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { EndpointDictionary } from 'app/utils/endpoints-dictionary';
import { environment } from 'environments/environment';
import {
  HttpService,
  LocalStorageService,
  MessageService,
  SessionService,
} from 'brainloper-ui';
import { HttpClient } from '@angular/common/http';
import { UserAuth } from 'app/models2/auth/user-auth';
import { Plan } from 'app/models2/configuration/plan';
import { Module } from 'app/models2/administration/module';
import { Configuration } from 'app/models2/auth/configuration';
import { Company } from 'app/models2/administration/company';
import { BaseResponse } from 'app/models2/commos/base-response';
import { RefreshToken } from 'app/models2/administration/refresh-token';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  constructor(
    private message: MessageService,
    private http: HttpService,
    private session: SessionService,
    private router: Router,
    private storage: LocalStorageService,
    private httpClient: HttpClient
  ) {}

  private isLoggingOut = false;

  public async getUserProfile(user: UserAuth): Promise<Boolean> {
    const companyIdActive = await this.http.getDataBody<boolean>(
      EndpointDictionary.Login.COMPANY_IS_ACTIVE
    );
    if (!companyIdActive) {
      this.Logout(true);
      return false;
    }
    this.login(user, EndpointDictionary.Login.GET_USER_PROFILE, false);
    return true;
  }

  public async login(
    user: UserAuth,
    url: string = EndpointDictionary.Login.LOGIN_DATA,
    navigateHome: boolean = true
  ): Promise<void> {
    this.isLoggingOut = false;
    try {
      let res: BaseResponse<Configuration> = await this.http.postDataPromise(
        url,
        user
      );
      if (res['code'] === 0) {
        this.session.setData('cargando', true);
        this.storage.set<Boolean>('initSession', true);
        if (res.body.token) this.storage.set<string>('token', res.body.token);
        if (res.body.refreshToken)
          this.storage.set<number>('refreshToken', res.body.refreshToken);
        this.storage.set<string>('rol', res.body.user.role);
        this.storage.set<number>('roleId', res.body.user.roleId);
        this.storage.set<string>('companyName', res.body.company.name);
        this.storage.set<string>('userName', res.body.user.name.split(' ')[0]);
        this.storage.set<number>('cedula', res.body.user.userId);
        this.storage.set<number>('userId', res.body.user.userId);
        this.storage.set<string>('user', res.body.user.user);
        this.storage.set<Company>('company', res.body.company);
        this.storage.set<number>('companyId', res.body.company.id);
        this.storage.set<Plan>('plan', res.body.plan);
        this.storage.set<Module[]>('modules', res.body.modules);
        this.storage.set<boolean>(
          'inventory_module',
          res.body.haveModuleInventory
        );
        this.storage.set<boolean>(
          'missingPaymentAlert',
          res.body.company.paymentAlert
        );
        this.session.setData('date', new Date());

        this.message.SuccessToast(res['message']);
        if (navigateHome) {
          // Verificar si hay una URL pendiente para redirigir tras el login
          const redirectUrl = this.storage.get<string>('postLoginRedirect');
          if (redirectUrl) {
            // Consumirla para que no se reutilice
            this.storage.remove('postLoginRedirect');
            // Si es una URL absoluta (ej: http://localhost:3000?eventId=510) usar window.location
            if (/^(http|https):\/\//i.test(redirectUrl)) {
              window.location.href = redirectUrl;
            } else {
              // Navegación interna de Angular
              this.router.navigateByUrl(redirectUrl);
            }
          } else {
            this.router.navigate(['inicio']);
          }
        }
        this.message.closeLoading();
      } else {
        this.message.Error(
          'Error de acceso',
          `Código: ${res['code']} - ${res['message']}`
        );
        this.message.closeLoading();
      }
    } catch (error) {
      this.message.Error('Error de acceso', '');
      this.Logout();
    }
  }

  private cleanClientSession(pendingRedirect: string | null, isCompanyInactive: boolean = false) {
    document.cookie.split(';').forEach((cookie) => {
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
      document.cookie =
        name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/';
    });

    // Limpiar localStorage
    this.storage.clear();
    sessionStorage.clear();

    this.router.navigate(['/']);
    this.message.closeLoading();

    if (isCompanyInactive) {
      this.message.Error('Error de acceso', 'Compañía inactiva');
    } else {
      this.message.Info('', 'Sesión finalizada');
    }

    this.storage.set<boolean>('initSession', false);
    this.storage.set<Date>('date', new Date());

    if (pendingRedirect) {
      this.storage.set<string>('postLoginRedirect', pendingRedirect);
    }
    this.isLoggingOut = false;
  }

  public async Logout(isCompanyInactive: boolean = false) {
    if (this.isLoggingOut) {
      return;
    }

    this.isLoggingOut = true;
    const pendingRedirect = this.storage.get<string>('postLoginRedirect');

    this.http
      .postData(EndpointDictionary.Login.LOGOUT, {}, null, true)
      .subscribe({
        next: () => this.cleanClientSession(pendingRedirect, isCompanyInactive),
        error: () => this.cleanClientSession(pendingRedirect, isCompanyInactive)
      });
  }

  public RefreshToken(token: string) {
    return this.httpClient.post<BaseResponse<RefreshToken>>(
      EndpointDictionary.Security.REFRESH_TOKEN,
      token
    );
  }
}
